import API from 'common/API'
import Connect from 'common/Connect'
import { BoardItem, BoardItemParams } from 'models/BoardItem'
import { BoardItemCreateRequest } from 'models/BoardItemCreateRequest'
import { BoardItemPermission } from 'models/BoardItemPermission'
import { BoardItemUpdateRequest } from 'models/BoardItemUpdateRequest'
import { SubsChannelProducts } from 'models/SubsChannelProducts'
import { FileAction } from './FileAction'

const BOARD_TYPE = 'custom-board'
const BOARD_MAX_SIZE = 40

export const BoardApiAction = {
    async get(userId: string, subsChannelProducts: SubsChannelProducts, page: number = 1): Promise<BoardItem[]> {
        const params = {
            board_type: BOARD_TYPE,
            page
        }
        const response = await Connect.get(API.v5External, `/ext-board/users/${ userId }/board-items`, params)
        return toBoardItems(response.data, subsChannelProducts)
    },

    async getAuthorized(userId: string, subsChannelProducts: SubsChannelProducts, page: number = 1): Promise<BoardItem[]> {
        const params = {
            board_type: BOARD_TYPE,
            limit: BOARD_MAX_SIZE,
            offset: BOARD_MAX_SIZE * (page - 1)
        }
        const response = await Connect.get(API.v5Authorized, `/ext-board/users/${ userId }/board-items`, params)
        return toBoardItems(response.data?.data?.items, subsChannelProducts)
    },

    async getMine(subsChannelProducts: SubsChannelProducts, offset: number = 0): Promise<BoardItem[]> {
        const params = {
            board_type: BOARD_TYPE,
            limit: BOARD_MAX_SIZE,
            offset: offset
        }
        const response = await Connect.get(API.v5Authorized, `/ext-board/users/me/board-items`, params)
        return toBoardItems(response.data?.data?.items, subsChannelProducts)
    },

    async post(request: BoardItemCreateRequest, subsChannelProducts: SubsChannelProducts): Promise<BoardItem> {
        const imageBase64 = await FileAction.convertToBase64(request.imageFile)
        const params = toCreateParams(request, imageBase64)
        const response = await Connect.post(API.v5Authorized, '/ext-board/users/me/board-items', params)
        return toBoardItem(response.data?.data?.items[0], subsChannelProducts)
    },

    async put(request: BoardItemUpdateRequest, subsChannelProducts: SubsChannelProducts): Promise<BoardItem> {
        const imageBase64 = await FileAction.convertToBase64(request.imageFile)
        const params = toUpdateParams(request, imageBase64)
        const response = await Connect.put(API.v5Authorized, `/ext-board/users/me/board-items/${ request.id }`, params)
        return toBoardItem(response.data?.data?.items[0], subsChannelProducts)
    },

    async sortToTop(id: string): Promise<void> {
        const params = { board_item_key: id }
        await Connect.put(API.v5Authorized, `/ext-board/users/me/board-items/sort`, params)
    },

    async delete(id: string): Promise<void> {
        await Connect.delete(API.v5Authorized, `/ext-board/users/me/board-items/${ id }`, {})
    },
}

const toBoardItems = (response: any, subsChannelProducts: SubsChannelProducts) => {
    return response.map((item: any) => toBoardItem(item, subsChannelProducts))
}

const toBoardItem = (response: any, subsChannelProducts: SubsChannelProducts) => {
    const permission = BoardItemPermission.create(
        response.permission === 'subscribers' ? 'subscribers' : 'all',
        subsChannelProducts,
        response.subs_product_id
    )
    const params: BoardItemParams = {
        id: response.board_item_key,
        title: response.title,
        description: response.description,
        image: {
            url: response.image?.url,
            width: response.image?.width,
            height: response.image?.height
        },
        uri: toUri(response.link_url),
        permission,
        hasPermission: response.has_permission ?? false,
        publishedAt: new Date(response.published_at)
    }

    return BoardItem.of(params)
}

const toUri = (response: any) => {
    try {
        const url = response ? new URL(response) : undefined
        return url && /https?:/.test(url.protocol) ? url : undefined
    } catch (e) {
        return undefined
    }
}

const toCreateParams = (request: BoardItemCreateRequest, imageBase64?: string) => ({
    permission: request.permission,
    link_url: request.linkUrl ?? '',
    subs_product_id: request.subsProductId ? request.subsProductId : undefined,
    title: request.title ?? '',
    description: request.description ?? '',
    image: imageBase64,
    board_type: BOARD_TYPE,
    enabled: true,
    position: 'top'
})

const toUpdateParams = (request: BoardItemUpdateRequest, imageBase64?: string) => ({
    permission: request.permission,
    subs_product_id: request.subsProductId ? request.subsProductId : undefined,
    link_url: request.linkUrl ?? '',
    title: request.title ?? '',
    description: request.description ?? '',
    image: request.isEmptyImage ? '' : imageBase64,
    enabled: true,
})
